import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Table, { TColumn, TSort } from '@components/table';
import Status from '@components/common/status';
import Tabs from '@components/tabs';
import RangeDatePicker from '@components/range_date_picker';
import MultiSelect from '@components/common/multi_select';
import moment from 'moment/moment';
import {
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType,
    TTransactionLite
} from '@xeppt/xeppt-sdk/types/transaction';
import {
    formatMoney,
    getTransactionAction,
    getTransactionMethod,
    getTransactionStatus,
    getTransactionStatusColor,
    getTransactionTypeLabel
} from '@utils/index';
import { transactionsStatuses } from '@const/index';
import { TFundsFlowBucket, TFundsFlowCurrencyBlock, TFundsFlowOpRow } from '@api';
import { useFundsFlow } from '@hooks/api/useFundsFlow';
import { useTransactions } from '@hooks/api/useTransactions';

export const getFundsFlowBucketLabel = (bucket: TFundsFlowBucket): string => {
    switch (bucket) {
        case 'in_edge':
            return 'Money In · external';
        case 'out_edge':
            return 'Money Out · external';
        case 'internal':
            return 'On-platform · internal ledger';
        default:
            return bucket;
    }
};

export const getFundsFlowBucketColor = (
    bucket: TFundsFlowBucket
): 'success' | 'warning' | 'blue' | 'gray' => {
    switch (bucket) {
        case 'in_edge':
            return 'success';
        case 'out_edge':
            return 'warning';
        case 'internal':
            return 'blue';
        default:
            return 'gray';
    }
};

// by_op is a small, single-response array (not paginated), so sorting happens
// entirely client-side — the Table component's sort/onSortChange props work the same
// way whether the caller re-sorts locally or refetches from the server.
export const sortFundsFlowOpRows = (rows: TFundsFlowOpRow[], sort: TSort): TFundsFlowOpRow[] => {
    const direction = sort.value === 'ASC' ? 1 : -1;
    const key = sort.key as keyof TFundsFlowOpRow;
    return [...rows].sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return (aValue - bValue) * direction;
        }
        return String(aValue).localeCompare(String(bValue)) * direction;
    });
};

export const buildKpiTiles = (block: TFundsFlowCurrencyBlock): { label: string; value: string }[] => [
    { label: 'Money In', value: formatMoney(block.kpis.money_in, block.currency) },
    { label: 'Money Out', value: formatMoney(block.kpis.money_out, block.currency) },
    { label: 'Net External', value: formatMoney(block.kpis.net_external, block.currency) },
    { label: 'Internal Volume', value: formatMoney(block.kpis.internal_volume, block.currency) },
    { label: 'Fees Earned', value: formatMoney(block.kpis.fees_earned, block.currency) },
    { label: 'Movement Count', value: block.kpis.movement_count.toLocaleString('en-US') }
];

const buildOpColumns = (currency: string): TColumn[] => [
    { label: 'Operation', key: 'label', isSort: true },
    {
        label: 'Bucket',
        key: 'bucket',
        render: (value: TFundsFlowBucket) => (
            <Status variant={getFundsFlowBucketColor(value)}>{getFundsFlowBucketLabel(value)}</Status>
        )
    },
    {
        label: 'Amount',
        key: 'amount',
        isSort: true,
        isRightAlignment: true,
        render: (value: number) => formatMoney(value, currency)
    },
    {
        label: 'Fee',
        key: 'fee',
        isSort: true,
        isRightAlignment: true,
        render: (value: number) => <span className={styles.fee_cell}>{formatMoney(value, currency)}</span>
    },
    {
        label: 'Count',
        key: 'count',
        isSort: true,
        isRightAlignment: true,
        render: (value: number) => value.toLocaleString('en-US')
    }
];

const movementColumns: TColumn[] = [
    {
        label: 'Date and Time',
        key: 'created_at',
        isSort: true,
        render: (value: string) => moment(value).format('MM.DD.YYYY HH:mm')
    },
    { label: 'Transaction ID', key: 'id' },
    {
        label: 'Method',
        key: 'method',
        render: (value: ETransactionMethod) => getTransactionMethod(value)
    },
    {
        label: 'Type',
        key: 'type',
        render: (value: ETransactionType) => getTransactionTypeLabel(value)
    },
    {
        label: 'Action',
        key: 'action',
        render: (_: number, row: TTransactionLite) => getTransactionAction(row.action)
    },
    {
        label: 'Status',
        key: 'status',
        render: (value: ETransactionStatus) => (
            <Status variant={getTransactionStatusColor(value)}>{getTransactionStatus(value)}</Status>
        )
    },
    { label: 'Currency', key: 'currency' },
    {
        label: 'Amount',
        key: 'amount',
        isRightAlignment: true,
        render: (value: number, row: TTransactionLite) => (
            <span className={row.type === ETransactionType.CREDIT ? styles.credit : styles.debit}>
                {formatMoney(value, row.currency)}
            </span>
        )
    }
];

const FundsFlowLayout = () => {
    const {
        filter,
        onChangeFilter,
        validationError,
        isLoading,
        error,
        currencies,
        selectedCurrency,
        setSelectedCurrency,
        selectedCurrencyBlock
    } = useFundsFlow();

    const [opSort, setOpSort] = useState<TSort>({ key: 'fee', value: 'DESC' });

    const {
        transactions,
        filter: movementFilter,
        onChangeFilter: onChangeMovementFilter,
        transactionsData,
        refetchTransactions
    } = useTransactions({ isInitialTransactions: false });

    // Keep the drill-down list scoped to the same date/status window as the KPIs —
    // composing the existing transactions hook/table rather than rebuilding either.
    useEffect(() => {
        if (validationError) return;
        onChangeMovementFilter({
            date_from: filter.date_from,
            date_to: filter.date_to,
            status: filter.statuses as ETransactionStatus[]
        });
    }, [filter.date_from, filter.date_to, filter.statuses, validationError]);

    const currencyTabs = currencies.map((block) => ({ label: block.currency, value: block.currency }));
    const sortedByOp = selectedCurrencyBlock
        ? sortFundsFlowOpRows(selectedCurrencyBlock.by_op, opSort)
        : [];

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">Funds Flow &amp; Fees</Typography>
                    <Typography variant="body2">Recorded funds movement (internal ledger)</Typography>
                </div>
            </div>

            <div className={styles.note}>
                <Typography variant="body3">
                    These figures show gross funds movement as recorded on XEPPT&apos;s internal
                    ledger. Fees are shown as charged at the time of the transaction — this view
                    does not yet net out provider sweeps or downstream adjustments. Phase 1
                    reporting.
                </Typography>
            </div>

            <div className={styles.filters}>
                <RangeDatePicker
                    values={[
                        filter.date_from ? filter.date_from.toISOString() : null,
                        filter.date_to ? filter.date_to.toISOString() : null
                    ]}
                    onChange={(val) =>
                        onChangeFilter({
                            date_from: val?.[0] ? new Date(val[0]) : undefined,
                            date_to: val?.[1] ? new Date(val[1]) : undefined
                        })
                    }
                />
                <MultiSelect
                    label="Status"
                    placeholder="All statuses"
                    values={filter.statuses}
                    items={transactionsStatuses}
                    onChange={(val) => onChangeFilter({ statuses: val as number[] })}
                />
            </div>
            {validationError && (
                <Typography variant="body3" className={styles.filter_error}>
                    {validationError}
                </Typography>
            )}

            {currencyTabs.length > 1 && (
                <Tabs
                    className={styles.currency_tabs}
                    tabs={currencyTabs}
                    activeTab={selectedCurrency ?? ''}
                    onChange={setSelectedCurrency}
                />
            )}

            {isLoading && !selectedCurrencyBlock && <Typography>Loading funds flow…</Typography>}
            {!isLoading && error && (
                <Typography variant="body3" className={styles.error_text}>
                    Couldn&apos;t load funds flow data. Try adjusting the filters or refreshing the
                    page.
                </Typography>
            )}
            {!isLoading && !error && currencies.length === 0 && (
                <Typography>No funds movement recorded for this period.</Typography>
            )}

            {selectedCurrencyBlock && (
                <>
                    <div className={styles.kpi_grid}>
                        {buildKpiTiles(selectedCurrencyBlock).map((tile) => (
                            <div className={styles.kpi_tile} key={tile.label}>
                                <Typography variant="body3" className={styles.kpi_label}>
                                    {tile.label}
                                </Typography>
                                <Typography variant="h5" className={styles.kpi_value}>
                                    {tile.value}
                                </Typography>
                            </div>
                        ))}
                    </div>

                    <div className={styles.section}>
                        <div className={styles.section_header}>
                            <Typography variant="h5" weight="bold">
                                Fees by operation
                            </Typography>
                        </div>
                        <Table
                            columns={buildOpColumns(selectedCurrencyBlock.currency)}
                            rows={sortedByOp}
                            sort={opSort}
                            onSortChange={(key, value) => setOpSort({ key, value })}
                            emptyDescription="No operations recorded for this period."
                        />
                    </div>
                </>
            )}

            <div className={styles.section}>
                <div className={styles.section_header}>
                    <Typography variant="h5" weight="bold">
                        Movement
                    </Typography>
                    <Typography variant="body3">
                        {transactionsData?.total ?? 0} records found
                    </Typography>
                </div>
                <Table
                    columns={movementColumns}
                    rows={transactions}
                    sort={{ key: 'created_at', value: movementFilter.order }}
                    onSortChange={() =>
                        onChangeMovementFilter({
                            order: movementFilter.order === 'DESC' ? 'ASC' : 'DESC'
                        })
                    }
                    totalRows={transactionsData?.total || 0}
                    onLoadData={() => refetchTransactions({ isNew: false, filter: movementFilter })}
                    emptyDescription="No transactions in this period."
                />
            </div>
        </div>
    );
};

export default FundsFlowLayout;
